import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { Flame, Send, Bookmark, ArrowUp } from "lucide-react";
import { api } from "@/lib/apiClient";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function MessageBubble({ msg, onSaveInsight, savedIds }) {
    const isUser = msg.role === "user";
    const isSaved = savedIds.has(msg.message_id);
    if (isUser) {
        return (
            <div className="flex justify-end animate-fade-in-up" data-testid={`msg-user-${msg.message_id}`}>
                <div className="max-w-[85%] rounded-2xl rounded-br-md bg-brand-darkPurple text-white px-5 py-3.5 shadow-soft-card">
                    <div className="whitespace-pre-wrap leading-relaxed text-[15px]">{msg.text}</div>
                </div>
            </div>
        );
    }
    return (
        <div className="flex gap-3 animate-fade-in-up" data-testid={`msg-ai-${msg.message_id}`}>
            <div className="shrink-0 mt-1">
                <div className="w-8 h-8 rounded-full bg-brand-creamDeep flex items-center justify-center">
                    <Flame className="w-4 h-4 text-brand-spark" strokeWidth={1.75} />
                </div>
            </div>
            <div className="flex-1 min-w-0">
                <div className="rounded-2xl rounded-tl-md bg-brand-creamDeep/85 border border-brand-darkPurple/8 px-5 py-4">
                    <div className="ai-prose whitespace-pre-wrap leading-relaxed text-[15px] text-brand-charcoal">{msg.text}</div>
                </div>
                <button
                    data-testid={`save-insight-btn-${msg.message_id}`}
                    onClick={() => onSaveInsight(msg)}
                    disabled={isSaved}
                    className={`mt-2 ml-2 inline-flex items-center gap-1.5 text-xs transition-colors ${
                        isSaved ? "text-brand-spark cursor-default" : "text-brand-charcoal/55 hover:text-brand-spark"
                    }`}
                >
                    <Bookmark className={`w-3.5 h-3.5 ${isSaved ? "fill-brand-spark" : ""}`} strokeWidth={1.75} />
                    {isSaved ? "Saved to your vault" : "Save as insight"}
                </button>
            </div>
        </div>
    );
}

export default function Chat() {
    const { conversationId } = useParams();
    const ctx = useOutletContext();
    const [messages, setMessages] = useState([]);
    const [conv, setConv] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [savedIds, setSavedIds] = useState(new Set());
    const scrollerRef = useRef(null);
    const textAreaRef = useRef(null);

    const scrollToBottom = useCallback(() => {
        requestAnimationFrame(() => {
            if (scrollerRef.current) {
                scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
            }
        });
    }, []);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/conversations/${conversationId}`);
            setConv(res.data.conversation);
            setMessages(res.data.messages || []);
            // Also load saved insights for this conversation to mark bookmarks
            const insRes = await api.get("/insights");
            const ids = new Set((insRes.data || []).filter(i => i.conversation_id === conversationId && i.message_id).map(i => i.message_id));
            setSavedIds(ids);
            setTimeout(scrollToBottom, 50);
        } catch (e) {
            toast.error("Could not load this space.");
        }
    }, [conversationId, scrollToBottom]);

    useEffect(() => { load(); }, [load]);

    const send = async () => {
        const t = text.trim();
        if (!t || sending) return;
        setSending(true);
        // Optimistic
        const optimistic = {
            message_id: `tmp_${Date.now()}`,
            role: "user",
            text: t,
            created_at: new Date().toISOString(),
        };
        setMessages((m) => [...m, optimistic]);
        setText("");
        scrollToBottom();
        try {
            const res = await api.post(`/conversations/${conversationId}/messages`, { text: t });
            setMessages((m) => {
                const withoutTmp = m.filter((x) => x.message_id !== optimistic.message_id);
                return [...withoutTmp, res.data.user_message, res.data.ai_message];
            });
            ctx?.refreshConversations?.();
            scrollToBottom();
        } catch (e) {
            setMessages((m) => m.filter((x) => x.message_id !== optimistic.message_id));
            const detail = e?.response?.data?.detail || "Something didn't send. Try again.";
            toast.error(detail);
            setText(t);
        } finally {
            setSending(false);
            textAreaRef.current?.focus();
        }
    };

    const handleSaveInsight = async (msg) => {
        try {
            await api.post("/insights", {
                text: msg.text,
                conversation_id: conversationId,
                message_id: msg.message_id,
                tags: [conv?.mode || "open"],
            });
            setSavedIds((s) => new Set([...s, msg.message_id]));
            toast.success("Saved to your vault.");
        } catch (e) {
            toast.error("Could not save.");
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const modeLabel = {
        untangle: "Untangling",
        prepare: "Preparing",
        stay_with_me: "Sitting with what's here",
        open: "Open conversation",
    }[conv?.mode] || "Conversation";

    const modeHint = {
        untangle: "Let's move slowly. Fact, then story, then body, then pattern, then what you actually want.",
        prepare: "We'll build the sentence you've been rehearsing. Together.",
        stay_with_me: "No strategy. Just here. Tell me what wants to be said.",
        open: "Wherever you are, that's where we start.",
    }[conv?.mode] || "";

    return (
        <div className="flex flex-col h-screen max-h-screen w-full" data-testid="chat-page">
            {/* Header */}
            <div className="border-b border-brand-darkPurple/10 bg-brand-cream/80 backdrop-blur-sm">
                <div className="max-w-3xl mx-auto px-6 md:px-10 py-5 pl-16 md:pl-10">
                    <div className="text-[11px] uppercase tracking-widest text-brand-charcoal/50">{modeLabel}</div>
                    <div className="mt-1 font-serif text-2xl text-brand-darkPurple tracking-tight truncate">{conv?.title || "New conversation"}</div>
                </div>
            </div>

            {/* Messages */}
            <div ref={scrollerRef} className="flex-1 overflow-y-auto chat-scroll">
                <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 space-y-6">
                    {messages.length === 0 && (
                        <div className="text-center py-16 animate-fade-in-up">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-creamDeep mb-5">
                                <Flame className="w-6 h-6 text-brand-spark animate-flicker" strokeWidth={1.5} />
                            </div>
                            <div className="font-serif italic text-xl text-brand-darkPurple max-w-lg mx-auto leading-snug">
                                {modeHint}
                            </div>
                        </div>
                    )}
                    {messages.map((m) => (
                        <MessageBubble
                            key={m.message_id}
                            msg={m}
                            onSaveInsight={handleSaveInsight}
                            savedIds={savedIds}
                        />
                    ))}
                    {sending && (
                        <div className="flex gap-3 animate-fade-in-up" data-testid="ai-thinking">
                            <div className="shrink-0 mt-1">
                                <div className="w-8 h-8 rounded-full bg-brand-creamDeep flex items-center justify-center">
                                    <Flame className="w-4 h-4 text-brand-spark animate-flicker" strokeWidth={1.75} />
                                </div>
                            </div>
                            <div className="rounded-2xl rounded-tl-md bg-brand-creamDeep/60 border border-brand-darkPurple/8 px-5 py-4">
                                <div className="flex gap-1.5 items-center h-5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-darkPurple/40 animate-pulse" style={{ animationDelay: "0ms" }} />
                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-darkPurple/40 animate-pulse" style={{ animationDelay: "180ms" }} />
                                    <span className="w-1.5 h-1.5 rounded-full bg-brand-darkPurple/40 animate-pulse" style={{ animationDelay: "360ms" }} />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Composer */}
            <div className="border-t border-brand-darkPurple/10 bg-brand-cream/95 backdrop-blur-sm">
                <div className="max-w-3xl mx-auto px-6 md:px-10 py-4">
                    <div className="relative rounded-2xl border border-brand-darkPurple/15 bg-white shadow-soft-card focus-within:border-brand-gold/60 transition-colors">
                        <Textarea
                            data-testid="chat-input"
                            ref={textAreaRef}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={onKey}
                            placeholder="Say what's true right now…"
                            className="min-h-[60px] max-h-40 border-0 shadow-none focus-visible:ring-0 resize-none pr-14 bg-transparent"
                        />
                        <button
                            data-testid="chat-send-btn"
                            onClick={send}
                            disabled={!text.trim() || sending}
                            className="absolute right-3 bottom-3 w-9 h-9 rounded-full bg-brand-darkPurple text-white hover:bg-brand-darkPurpleSoft disabled:opacity-40 flex items-center justify-center transition-colors"
                            aria-label="Send"
                        >
                            <ArrowUp className="w-4 h-4" strokeWidth={2} />
                        </button>
                    </div>
                    <div className="mt-2 text-[11px] text-brand-charcoal/45 text-center">Press Enter to send · Shift+Enter for a new line</div>
                </div>
            </div>
        </div>
    );
}
